import {Injectable, inject} from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { bridgeKeycloakToMediaCookie } from '../interceptors/media-fetch-auth';

@Injectable({
    providedIn: 'root'
})
export class TokenRefreshService {
    private keycloakService = inject(KeycloakService);

    private refreshSubscription: Subscription | null = null;
    private isRefreshing = false;
    private readonly TOKEN_REFRESH_THRESHOLD = 60;
    private readonly CHECK_INTERVAL = 15000;

    // Réémission du cookie /media/ (KeycloakCookieBridgeView) : indépendante
    // du rafraîchissement du token Keycloak lui-même (dont la durée de vie,
    // configurée côté royaume Keycloak, peut être bien plus longue que les 5
    // minutes du JWT contenu dans ce cookie) — sinon les <img src="/media/...">
    // se remettraient à 401 entre deux refresh Keycloak trop espacés.
    private readonly MEDIA_COOKIE_REBRIDGE_INTERVAL = 4 * 60 * 1000;
    private lastMediaCookieBridgeAt = 0;

    /**
     * Démarre le service de surveillance et rafraîchissement du token
     */
    public startTokenRefreshMonitoring(): void {
        this.stopTokenRefreshMonitoring();

        // main.ts a déjà posé le cookie /media/ au bootstrap : éviter un
        // second appel immédiat et inutile au premier tick du poll.
        this.lastMediaCookieBridgeAt = Date.now();

        this.isRefreshing = true;
        this.refreshSubscription = interval(this.CHECK_INTERVAL)
            .pipe(takeWhile(() => this.isRefreshing))
            .subscribe(() => {
                this.checkAndRefreshToken();
            });
    }

    /**
     * Arrête le service de surveillance
     */
    public stopTokenRefreshMonitoring(): void {
        if (this.refreshSubscription) {
            this.isRefreshing = false;
            this.refreshSubscription.unsubscribe();
            this.refreshSubscription = null;
        }
    }

    /**
     * Vérifie si le token a besoin d'être rafraîchi et le rafraîchit si nécessaire
     */
    private checkAndRefreshToken(): void {
        if (!this.keycloakService.isLoggedIn()) {
            return;
        }
        const keycloakInstance = this.keycloakService.getKeycloakInstance();
        if (!keycloakInstance || !keycloakInstance.token) {
            return;
        }
        const tokenExpiresIn = this.getTokenExpirationTime();
        if (tokenExpiresIn <= this.TOKEN_REFRESH_THRESHOLD) {
            this.keycloakService.updateToken()
                .catch(error => {
                    console.error('Erreur lors du rafraîchissement du token', error);
                    this.keycloakService.logout();
                });
        }

        if (Date.now() - this.lastMediaCookieBridgeAt >= this.MEDIA_COOKIE_REBRIDGE_INTERVAL) {
            this.lastMediaCookieBridgeAt = Date.now();
            bridgeKeycloakToMediaCookie(this.keycloakService);
        }
    }

    /**
     * Calcule le temps restant avant l'expiration du token en secondes
     */
    public getTokenExpirationTime(): number {
        const keycloakInstance = this.keycloakService.getKeycloakInstance();
        if (!keycloakInstance || !keycloakInstance.tokenParsed) {
            return 0;
        }
        const expiresAt = keycloakInstance.tokenParsed['exp'];
        const now = Math.floor(Date.now() / 1000);
        return expiresAt - now;
    }
}